// src/components/systemdesign/SystemDesign.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SECTIONS } from "./systemDesignChecklist";

/**
 * SystemDesign (Sponsored on RIGHT)
 * - LEFT (xl+): sticky "On this page"
 * - MAIN: search + expand/collapse + section cards
 * - RIGHT (lg+): Sponsored Ad (sticky)
 *
 * Optional env toggle:
 *   REACT_APP_ENABLE_ADS=true|false
 */

// ================== AdSense helpers ==================
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

// ================== Page bits ==================
const normalize = (s = "") => String(s).toLowerCase();

function OnThisPageLeft({ sections }) {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>

        <nav
          className="nav flex-column small"
          style={{ maxHeight: "80vh", overflowY: "auto" }}
        >
          <a className="nav-link px-0" href="#sd-top">
            Overview
          </a>
          <a className="nav-link px-0" href="#sd-controls">
            Search &amp; Controls
          </a>
          <a className="nav-link px-0 mb-2" href="#sd-checklist">
            Checklist
          </a>

          <div className="text-uppercase text-muted small mt-2 mb-1">
            Sections
          </div>

          {sections.map((s) => (
            <a key={s.slug} className="nav-link px-0" href={`#sd-${s.slug}`}>
              {s.id}) {s.title}
            </a>
          ))}
        </nav>
      </div>
    </div>
  );
}

function RightSponsoredCard() {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">Sponsored</h2>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

// ================== Main component ==================
export default function SystemDesign() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(() => new Set(SECTIONS.map((s) => s.slug))); // all open

  const filtered = useMemo(() => {
    if (!q.trim()) return SECTIONS;
    const needle = normalize(q);

    return SECTIONS.filter(
      (s) =>
        normalize(s.title).includes(needle) ||
        normalize(s.summary).includes(needle) ||
        (Array.isArray(s.bullets) &&
          s.bullets.some((b) => normalize(b).includes(needle)))
    );
  }, [q]);

  const toggle = (slug) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });

  const expandAll = () => setOpen(new Set(SECTIONS.map((s) => s.slug)));
  const collapseAll = () => setOpen(new Set());

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page (xl+) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft sections={SECTIONS} />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main" id="sd-top">
          {/* hero */}
          <header className="mb-4 text-center">
            <span className="badge bg-dark text-white mb-2">System Design</span>
            <h1 className="h2 mb-2">System Design Interview — Master Checklist</h1>
            <p className="text-muted mb-0">
              Click a section title to open its detailed page, or expand here to skim the sub-topics.
            </p>
          </header>

          {/* controls */}
          <section id="sd-controls" className="mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body d-flex flex-wrap align-items-center gap-2">
                <div className="input-group" style={{ maxWidth: 520 }}>
                  <span className="input-group-text">🔎</span>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search (e.g., caching, CAP, load balancer, RTO)…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    aria-label="Search system design topics"
                  />
                </div>

                <div className="ms-auto d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={expandAll}
                    type="button"
                  >
                    Expand all
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={collapseAll}
                    type="button"
                  >
                    Collapse all
                  </button>
                </div>
              </div>
            </div>

            <div className="small text-muted mt-2">
              Showing <strong>{filtered.length}</strong> of{" "}
              <strong>{SECTIONS.length}</strong> sections
            </div>
          </section>

          {/* list */}
          <section id="sd-checklist">
            <div className="row g-3">
              {filtered.map((s) => (
                <div className="col-12" key={s.slug} id={`sd-${s.slug}`}>
                  <section className="card shadow-sm border-0 mb-3">
                    <div className="card-body">
                      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                        <div>
                          <h2 className="h5 mb-1">
                            <Link to={`/${s.slug}`} className="text-decoration-none">
                              {s.id}) {s.title} ↗
                            </Link>
                          </h2>
                          <div className="text-muted">{s.summary}</div>
                        </div>

                        <div className="d-flex gap-2">
                          <Link to={`/${s.slug}`} className="btn btn-primary btn-sm">
                            Open details
                          </Link>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => toggle(s.slug)}
                            aria-expanded={open.has(s.slug)}
                            aria-controls={`section-${s.slug}`}
                          >
                            {open.has(s.slug) ? "Hide bullets" : "Show bullets"}
                          </button>
                        </div>
                      </div>

                      {open.has(s.slug) && (
                        <ul
                          id={`section-${s.slug}`}
                          className="mt-3 mb-0 small text-muted"
                        >
                          {s.bullets.map((b, i) => (
                            <li key={i} className="mb-1">
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </section>
                </div>
              ))}
            </div>
          </section>

          {/* In-article ad (optional) */}
          {ADS_ENABLED && (
            <div className="my-3 text-center">
              <AdSlot
                enabled={ADS_ENABLED}
                adSlot="5201219465"
                style={{ display: "block", minHeight: 90, textAlign: "center" }}
              />
            </div>
          )}

          <footer className="border-top mt-4 pt-3 small text-muted text-center">
            Pro tip: Use this checklist as a repeatable framework for every system design prompt.
          </footer>
        </main>

        {/* RIGHT: Sponsored (lg+) */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 pt-3 border-start">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
